import { useFloating, offset, flip, size, autoUpdate } from '@floating-ui/react';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Menu.module.scss.js';
import { MenuContents } from './MenuContents.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useControllableState } from '../util/useControllableState.js';
import { useA11y } from '../provider/A11yProvider.js';
import { useItemTree } from '../util/ItemTree/useItemTree.js';
import { ItemTree } from '../util/ItemTree/ItemTree.js';
import { Dialog } from '../dialog/Dialog.js';
import classNames from 'clsx';

function Menu(props) {
  const {
    alignment = 'bottom',
    anchor,
    children,
    className: callerClassName,
    closeOnSelect = true,
    size: size$1 = 'default',
    tag,
    onOpen,
    onClose,
    onSelect,
    UNSAFE_layout
  } = props;
  const [state, setState] = useControllableState(props.state, 'closed', state => {
    if (state === 'open') {
      onOpen?.();
    } else {
      setActiveIndex(null);
      setActiveSubmenus([]);
      onClose?.();
    }
  });

  /* The list of indices of currently open submenus */
  const [activeSubmenus, setActiveSubmenus] = useState([]);

  /* The index of the current keyboard-selected menu item within the active submenu */
  const [activeIndex, setActiveIndex] = useState(null);
  const {
    portalTarget
  } = useA11y();
  const {
    floatingStyles,
    refs,
    elements,
    update
  } = useFloating({
    open: state === 'open',
    placement: `${alignment}-start`,
    strategy: 'fixed',
    middleware: [offset(4), flip(), size({
      apply(_ref) {
        let {
          availableHeight,
          elements
        } = _ref;
        Object.assign(elements.floating.style, {
          maxHeight: `${Math.max(50, availableHeight - 8)}px`
        });
      }
    })]
  });
  useEffect(() => {
    if (state === 'open' && elements.reference && elements.floating) {
      const cleanup = autoUpdate(elements.reference, elements.floating, update);
      return cleanup;
    }
  }, [state, elements, update]);
  const items = useItemTree();
  const anchorEl = anchor({
    ref: refs.setReference,
    tabIndex: 0,
    onBlur: handleAnchorBlur,
    onClick: handleAnchorClick,
    onKeyDown: handleKeyDown,
    'data-dsx--menu': tag
  });
  const noScroll = activeSubmenus.length;
  const menuRef = useRef(null);
  const hasOverflow = menuRef.current && menuRef.current.clientHeight < menuRef.current.scrollHeight;
  const layout = UNSAFE_layout ?? (CSS.supports('grid-template-columns', 'subgrid') ? 'grid' : 'table');
  const dialogClassName = classNames('dsx-component', styles['dialog'], callerClassName);
  const menuClassName = classNames(styles['menu'], styles[`size-${size$1}`], noScroll && styles['no-scroll'], hasOverflow && styles['has-overflow']);
  function handleAnchorBlur() {
    if (state === 'open') {
      setState('closed');
    }
  }
  function handleAnchorClick() {
    setState(state === 'open' ? 'closed' : 'open');
  }
  function handleKeyDown(event) {
    const action = getActionFromKeyboardEvent(event, state === 'open');
    switch (action) {
      case 'open':
        setState('open');
        event.preventDefault();
        break;
      case 'close':
        setState('closed');
        event.preventDefault();
        break;
      case 'select':
        handleKeyboardSelect();
        event.preventDefault();
        break;
      case 'in':
        handleKeyboardIn();
        event.preventDefault();
        break;
      case 'out':
        handleKeyboardOut();
        event.preventDefault();
        break;
      case 'previous':
        handleKeyboardNavigate(activeSubmenus, activeIndex ?? 0, -1);
        event.preventDefault();
        break;
      case 'next':
        handleKeyboardNavigate(activeSubmenus, activeIndex ?? -1, 1);
        event.preventDefault();
        break;
      case 'first':
        handleKeyboardNavigate(activeSubmenus, -1, 1);
        event.preventDefault();
        break;
      case 'last':
        handleKeyboardNavigate(activeSubmenus, 0, -1);
        event.preventDefault();
        break;
    }
  }
  function getActiveList(activeSubmenus) {
    let activeList = items.collection;
    for (const index of activeSubmenus) {
      activeList = activeList[index].children;
    }
    return activeList;
  }
  function handleKeyboardSelect() {
    if (activeIndex == null) {
      return;
    }
    const activeList = getActiveList(activeSubmenus);
    const activeItem = activeList[activeIndex];
    if (activeItem.data.type === 'menu-item') {
      handleSelectItem(activeItem.data.props.value);
    } else if (activeItem.data.type === 'submenu') {
      handleSelectSubmenu([...activeSubmenus, activeIndex], true);
    }
  }
  function handleKeyboardIn() {
    if (activeIndex == null) {
      return;
    }
    const activeList = getActiveList(activeSubmenus);
    const activeItem = activeList[activeIndex];
    if (activeItem.data.type === 'submenu') {
      const submenus = [...activeSubmenus, activeIndex];
      setActiveSubmenus(submenus);
      handleKeyboardNavigate(submenus, -1, 1);
    }
  }
  function handleKeyboardOut() {
    if (activeSubmenus.length) {
      setActiveIndex(activeSubmenus.at(-1));
      setActiveSubmenus(activeSubmenus.slice(0, -1));
    }
  }
  function handleKeyboardNavigate(activeSubmenus, startIndex, dIndex) {
    const activeList = getActiveList(activeSubmenus);
    startIndex = (startIndex + activeList.length) % activeList.length;
    let index = startIndex;
    do {
      index = (index + dIndex + activeList.length) % activeList.length;
      const item = activeList[index];
      if (item.data.type == 'menu-item' && !item.data.props.disabled || item.data.type == 'submenu' && !item.data.props.disabled) {
        setActiveIndex(index);
        break;
      }
    } while (index !== startIndex);
  }
  function handleSelectItem(value) {
    onSelect(value);
    if (closeOnSelect) {
      setState('closed');
    }
  }
  function handleSelectSubmenu(value) {
    let keyboardTriggered = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (value.every((n, i) => activeSubmenus[i] === n)) {
      setActiveSubmenus(value.slice(0, -1));
      setActiveIndex(keyboardTriggered ? value.at(-1) : null);
    } else {
      setActiveSubmenus(value);
      if (keyboardTriggered) {
        handleKeyboardNavigate(value, -1, 1);
      } else {
        setActiveIndex(null);
      }
    }
  }
  function preventBlur(event) {
    // Note: this will prevent :active styling on menu items in Firefox:
    // https://bugzilla.mozilla.org/show_bug.cgi?id=771241
    event.preventDefault();
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(ItemTree, {
      items: items,
      children: children
    }), anchorEl, portalTarget && /*#__PURE__*/createPortal(/*#__PURE__*/jsx(Dialog, {
      className: dialogClassName,
      focusOnOpen: false,
      isOpen: state === 'open',
      ref: refs.setFloating,
      style: floatingStyles,
      onMouseDown: preventBlur,
      "data-dsx--menu--popover": tag,
      children: /*#__PURE__*/jsx("div", {
        className: menuClassName,
        ref: menuRef,
        children: /*#__PURE__*/jsx(MenuContents, {
          activeIndex: activeIndex,
          activeSubmenus: activeSubmenus,
          items: items.collection,
          size: size$1,
          layout: layout,
          path: [],
          onSelectItem: handleSelectItem,
          onSelectSubmenu: handleSelectSubmenu
        })
      })
    }), portalTarget)]
  });
}
function getActionFromKeyboardEvent(event, open) {
  const {
    key
  } = event;
  if (open) {
    switch (key) {
      case 'Escape':
        return 'close';
      case 'Enter':
      case ' ':
        return 'select';
      case 'ArrowUp':
        return 'previous';
      case 'ArrowDown':
        return 'next';
      case 'ArrowRight':
        return 'in';
      case 'ArrowLeft':
        return 'out';
      case 'PageUp':
        return 'first';
      case 'PageDown':
        return 'last';
      default:
        return null;
    }
  } else {
    switch (key) {
      case 'Enter':
      case ' ':
      case 'ArrowUp':
      case 'ArrowDown':
      case 'PageUp':
      case 'PageDown':
        return 'open';
      default:
        return null;
    }
  }
}

export { Menu, getActionFromKeyboardEvent };
